import { useWSMgrContext } from "@/providers/WsMgrProvider";

/**
 * React-side wrapper around `wsMgr.fPermissions` (QPermissions). The
 * previous implementation reimplemented every permission derivation
 * inline here; now it consumes the coreapp class directly so there's
 * exactly one derivation path. See coreapp/CoreApp/QPermissions.js.
 *
 * useWSMgrContext triggers re-render when wsMgr context state changes
 * (login, chart swap, etc.), and QPermissions methods read live state
 * on each call — so reactivity is preserved end-to-end. Consumers that
 * used individual flags (`canEditDocuments`, `isAdmin`, …) continue to
 * read them unchanged.
 */
export const usePermissionCheck = () => {
  const wsMgrContext = useWSMgrContext();
  const wsMgr = wsMgrContext.wsMgr.state;
  const perms = wsMgr?.fPermissions;

  return {
    canAccessEdit: !!perms?.canAccessEdit(),
    canAccessPIT: !!perms?.canAccessPIT(),
    canAccessPlanning: !!perms?.canAccessPlanning(),
    canAccessReporting: !!perms?.canAccessReporting(),
    canAccessDirectory: !!perms?.canAccessDirectory(),
    hasIFH: !!perms?.hasIFH(),
    canAccessSavedFilters: !!perms?.canAccessSavedFilters(),
    canAccessSavedHighlights: !!perms?.canAccessSavedHighlights(),
    canAccessInteractiveFilters: !!perms?.canAccessInteractiveFilters(),
    canAccessInteractiveHighlights: !!perms?.canAccessInteractiveHighlights(),
    shouldShowUpgradeFilters: !!perms?.shouldShowUpgradeFilters(),
    shouldShowUpgradeHighlights: !!perms?.shouldShowUpgradeHighlights(),
    canAccessTemplates: !!perms?.canAccessTemplates(),
    canAccessBackground: !!perms?.canAccessBackground(),
    canAccessPosMgmt: !!perms?.canAccessPosMgmt(),
    canEnterBackgroundEditor: !!perms?.canEnterBackgroundEditor(),
    canViewRecordSourceDetails: !!perms?.canViewRecordSourceDetails(),
    canEditDocuments: !!perms?.canEditDocuments(),
    canAccessChartSettings: !!perms?.canAccessChartSettings(),
    canAccessViewLibrary: !!perms?.canAccessViewLibrary(),
    canAccessGrouping: !!perms?.canAccessGrouping(),
    canAccessAbout: !!perms?.canAccessAbout(),
    canAccessInteractiveFields: !!perms?.canAccessInteractiveFields(),
    canAccessProfileCard: !!perms?.canAccessProfileCard(),
    canAccessRecordDetails: !!perms?.canAccessRecordDetails(),
    hasActiveBLS: !!perms?.hasActiveBLS(),
    canAccessFM: !!perms?.canAccessFM(),
    canModifyBackgrounds: !!perms?.canModifyBackgrounds(),
    canAccessChartExplorer: !!perms?.canAccessChartExplorer(),
    canDeleteAllFolderContents: !!perms?.canDeleteAllFolderContents(),
    fileManagerPermissions: perms?.fileManagerPermissions() ?? [],
    isSupportLogin: !!perms?.isSupportLogin(),
    canAccessTalentPool: !!perms?.canAccessTalentPool(),
    canAccessChanges: !!perms?.canAccessChanges(),
    canAccessPlanningChanges: !!perms?.canAccessPlanningChanges(),
    canAccessStaging: !!perms?.canAccessStaging(),
    canAccessPlanApprovals: !!perms?.canAccessPlanApprovals(),
    shouldShowPlanApprovals: !!perms?.shouldShowPlanApprovals(),
    canAccessBoxLibrary: !!perms?.canAccessBoxLibrary(),
    canUpdatePlan: !!perms?.canUpdatePlan(),
    showPricingAndPackaging: !!perms?.showPricingAndPackaging(),
    canAccessWebLink: !!perms?.canAccessWebLink(),
    canAccessSubcharts: !!perms?.canAccessSubcharts(),
    canAccessViewSwitcher: !!perms?.canAccessViewSwitcher(),
    canAccessAdvancedSearch: !!perms?.canAccessAdvancedSearch(),
    canExport: !!perms?.canExport(),
    shouldShowPlanning: !!perms?.shouldShowPlanning(),
    shouldShowPIT: !!perms?.shouldShowPIT(),
    shouldShowPlanSettings: !!perms?.shouldShowPlanSettings(),
    shouldShowUpgradePlanning: !!perms?.shouldShowUpgradePlanning(),
    isAdmin: !!perms?.isAdmin(),
    isFullAccess: !!perms?.isFullAccess(),
    canShareFilters: !!perms?.canShareFilters(),
    canAutoExport: !!perms?.canAutoExport(),
    showFunctionalStatement: !!perms?.showFunctionalStatement(),
    showChartNavigator: !!perms?.showChartNavigator(),
    showSidebarFilters: !!perms?.showSidebarFilters(),
    showSidebarHighlights: !!perms?.showSidebarHighlights(),
    showSidebarBackground: !!perms?.showSidebarBackground(),
    showSidebarEmployeeList: !!perms?.showSidebarEmployeeList(),
    showViews: !!perms?.showViews(),
    shouldShowNavigation: !!perms?.shouldShowNavigation()
  };
};
